import React, { useState } from 'react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { cn } from '../../../tailwind-base/utils';
import { Button } from '../../shadcn/Button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '../../shadcn/Carousel';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import YoutubeVideo from '../../YoutubeVideo';
import breakpoints from '../../breakpoints';

const isBrowser = typeof window !== 'undefined';

interface VideoSelectButtonProps {
  handleTabClick: (arg: number) => void;
  index: number;
  isSelected: boolean;
  video: { _key: string; caption: string };
}

const VideoSelectButton = ({
  handleTabClick,
  index,
  isSelected,
  video,
}: VideoSelectButtonProps) => {
  const { tt } = useUiComponentsInternationalContext();

  return (
    <CarouselItem className="pt-0 pl-0 lg:max-w-fit">
      <Button
        aria-label={tt(
          s => s.pdpSpecificVideoLockupComponent.selectVideoAriaLabel,
          {
            caption: video?.caption,
          }
        )}
        className={cn(
          'w-full lg:max-w-80',
          isSelected ? 'cursor-auto hover:bg-secondary' : ''
        )}
        key={video?._key}
        onClick={() => handleTabClick(index)}
        variant={isSelected ? 'secondary' : 'outline'}
      >
        <span className="text-lg font-bold overflow-hidden text-ellipsis">
          {video?.caption ||
            tt(s => s.pdpSpecificVideoLockupComponent.learnMoreButton)}
        </span>
      </Button>
    </CarouselItem>
  );
};

interface Props {
  block: {
    videos: {
      _key: string;
      _type: string;
      caption: string;
      id: string;
      thumbnail: string;
      url: string;
    }[];
  };
}

export const PdpSpecificVideoLockup = ({ block }: Props) => {
  const initialVideosState = block?.videos?.map(() => ({
    duration: 0,
    isPlaying: false,
    progress: 0,
  }));

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [videoApi, setVideoApi] = useState<CarouselApi>();
  const [videosState, setVideosState] = useState(initialVideosState);

  const isDesktop =
    isBrowser && window.matchMedia(`(min-width: ${breakpoints.lg})`).matches;

  const handlePauseAllVideos = () => {
    setVideosState(pvs => pvs.map(vs => ({ ...vs, isPlaying: false })));
  };

  const updateVideoState = (index, updates) => {
    setVideosState(pvs => {
      const newVideosState = [...pvs];
      newVideosState[index] = { ...newVideosState[index], ...updates };
      return newVideosState;
    });
  };

  const handlePlayPause = index => {
    handlePauseAllVideos();
    updateVideoState(index, { isPlaying: !videosState[index].isPlaying });
  };

  const handleTabClick = index => {
    if (videosState[selectedTabIndex].isPlaying) {
      handlePlayPause(selectedTabIndex);
    }

    setSelectedTabIndex(index);
    videoApi?.scrollTo(index);
  };

  return (
    <div className="py-10">
      <Carousel opts={{ watchDrag: false }} setApi={setVideoApi}>
        <CarouselContent>
          {block?.videos?.map(video => (
            <CarouselItem key={video._key}>
              <YoutubeVideo id={video.id} title={video.caption} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <Carousel
        orientation={isDesktop ? 'horizontal' : 'vertical'}
        plugins={[WheelGesturesPlugin()]}
      >
        <CarouselContent className="flex lg:hidden flex-col gap-2 p-1 pt-5">
          {block?.videos?.map((video, index) => (
            <VideoSelectButton
              key={video._key}
              handleTabClick={handleTabClick}
              index={index}
              isSelected={selectedTabIndex === index}
              video={video}
            />
          ))}
        </CarouselContent>
        <div className="relative">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-transparent to-background via-80% pointer-events-none z-10" />
          <CarouselContent className="hidden lg:flex items-center py-1 pl-5 mr-24 relative gap-2 cursor-grab active:cursor-grabbing">
            {block?.videos?.map((video, index) => (
              <VideoSelectButton
                key={video._key}
                handleTabClick={handleTabClick}
                index={index}
                isSelected={selectedTabIndex === index}
                video={video}
              />
            ))}
          </CarouselContent>
        </div>
      </Carousel>
    </div>
  );
};
